import { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Icon } from '@/lib/icons';
import type { LeaderboardCopy } from '../leaderboard-copy';
import { ALL_LANGUAGES, type LeaderboardPeriod } from '../leaderboard.types';

interface LeaderboardFiltersProps {
  copy: LeaderboardCopy;
  languages: { code: string; displayName: string }[];
  language: string;
  onLanguageChange: (code: string) => void;
  period: LeaderboardPeriod;
  onPeriodChange: (period: LeaderboardPeriod) => void;
  search: string;
  onSearchChange: (query: string) => void;
}

/**
 * One row of filters that wraps cleanly: the two Selects share a line on a
 * phone, the search takes the next one.
 */
export function LeaderboardFilters({
  copy,
  languages,
  language,
  onLanguageChange,
  period,
  onPeriodChange,
  search,
  onSearchChange,
}: LeaderboardFiltersProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const periods: { value: LeaderboardPeriod; label: string }[] = [
    { value: 'week', label: copy.periodWeek },
    { value: 'month', label: copy.periodMonth },
    { value: 'all', label: copy.periodAll },
  ];

  return (
    <div role="group" aria-label={copy.filtersLabel} className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
      <Select value={language} onValueChange={onLanguageChange}>
        <SelectTrigger aria-label={copy.languageLabel} className="h-10 min-w-0 bg-surface sm:w-48">
          <SelectValue placeholder={copy.languageLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_LANGUAGES}>{copy.allLanguages}</SelectItem>
          {languages.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              {lang.displayName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={period} onValueChange={(value) => onPeriodChange(value as LeaderboardPeriod)}>
        <SelectTrigger aria-label={copy.periodLabel} className="h-10 min-w-0 bg-surface sm:w-44">
          <SelectValue placeholder={copy.periodLabel} />
        </SelectTrigger>
        <SelectContent>
          {periods.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative col-span-2 sm:ml-auto sm:w-64">
        <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint-foreground" />
        <Input
          ref={inputRef}
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={copy.searchLabel}
          aria-label={copy.searchLabel}
          className="h-10 bg-surface pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden"
        />
        {search ? (
          <button
            type="button"
            onClick={() => {
              onSearchChange('');
              inputRef.current?.focus();
            }}
            aria-label={copy.clearSearch}
            className="absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded text-faint-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Icon name="x" className="size-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
